import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import VideoEmbed from "@/components/VideoEmbed";
import { BRANDS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export default function MarcaDetalhe() {
  const { slug } = useParams();
  const { t, lp } = useLanguage();
  const brand = BRANDS.find((b) => b.slug === slug);

  if (!brand) return <Navigate to={lp("/marcas")} replace />;

  const others = BRANDS.filter((b) => b.slug !== slug);

  return (
    <div data-testid={`brand-detail-${slug}`} className="pt-20">
      <Seo title={`${brand.name} — ${t(`brand.${slug}.tag`)} | IGNIVA`} description={t(`brand.${slug}.short`)} />

      <section className="relative h-[62vh] min-h-[420px] overflow-hidden border-b border-white/10">
        <Img id={brand.image} alt={brand.name} label={t("common.pending_image")} priority className="absolute inset-0 w-full h-full object-cover img-tone anim-drift" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121316] via-[#121316]/45 to-[#121316]/10" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-6 pb-12">
          <Link to={lp("/marcas")} data-testid="brand-back-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#A8A6A0] hover:text-white transition-colors mb-6">
            <ArrowLeft size={13} aria-hidden="true" /> {t("common.back_brands")}
          </Link>
          <span className="mono-tag block text-[10px] text-[#C2A675] uppercase tracking-[0.22em] mb-3">{t(`brand.${slug}.tag`)}</span>
          {brand.exclusive && <span className="mono-tag inline-block text-[10px] text-[#121316] uppercase tracking-[0.2em] px-2.5 py-1 bg-[#C2A675] mb-4">{t("common.exclusive_pt")}</span>}
          {brand.logo && (
            <img src={brand.logo} alt={`${brand.name} — logótipo oficial`} data-testid="brand-official-logo" className="h-10 sm:h-12 w-auto mb-4 object-contain object-left" loading="eager" decoding="async" />
          )}
          <h1 className="serif-display text-5xl sm:text-6xl lg:text-7xl font-light text-white">{brand.name}</h1>
          <p className="text-xs uppercase tracking-[0.18em] text-[#D6D3CC] mt-3">{t(`brand.${slug}.origin`)}</p>
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="serif-display text-3xl sm:text-4xl text-white font-light leading-tight mb-6" data-testid="brand-headline">{t(`brand.${slug}.headline`)}</p>
            {t(`brand.${slug}.long`).map((p, i) => (
              <p key={i} className="text-base text-[#A8A6A0] font-light leading-relaxed mb-5">{p}</p>
            ))}
          </Reveal>

          <Reveal delay={0.05} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-4">{t("common.categories")}</p>
              <ul className="space-y-2" data-testid="brand-categories">
                {t(`brand.${slug}.categories`).map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-[#E8E6E1] font-light"><span className="w-1.5 h-1.5 bg-[#C2A675] mt-2 shrink-0" aria-hidden="true" />{c}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-4">{t("common.scopes")}</p>
              <ul className="flex flex-wrap gap-2" data-testid="brand-scopes">
                {t(`brand.${slug}.ambitos`).map((a) => (
                  <li key={a} className="px-3 py-1.5 border border-white/15 text-xs text-[#E8E6E1] font-light">{a}</li>
                ))}
              </ul>
            </div>
          </Reveal>

          {brand.video && (
            <Reveal delay={0.1} className="mt-12">
              <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-4">{t("common.video")}</p>
              <VideoEmbed id={brand.video} title={`${brand.name} — ${t("common.video")}`} hint={t("common.video_hint")} />
              <p className="text-[11px] text-[#A8A6A0]/80 font-light mt-3">{t("common.video_note")}</p>
            </Reveal>
          )}

          <Reveal delay={0.15} className="mt-12">
            <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-4">{t("common.gallery")}</p>
            <div className="grid grid-cols-2 gap-3 sm:gap-5" data-testid="brand-gallery">
              {brand.gallery.map((id, i) => (
                <div key={i} className={`img-frame border border-white/10 ${i === 0 ? "aspect-[4/5]" : "aspect-[4/5] mt-8"}`}>
                  <Img id={id} alt={brand.name} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#A8A6A0]/80 font-light mt-4">{brand.image ? t("common.image_note") : t("common.pending_image")}</p>
          </Reveal>
        </div>

        <aside className="lg:col-span-5">
          <Reveal delay={0.15} className="bg-[#181A1F] border border-white/10 p-8 space-y-6 lg:sticky lg:top-28">
            <div>
              <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-3">{t("common.catalogue")}</p>
              <p className="text-sm text-[#A8A6A0] font-light leading-relaxed flex items-start gap-2">
                <FileText size={14} className="shrink-0 mt-0.5 text-[#C2A675]" aria-hidden="true" />
                {t("common.catalogue_note")}
              </p>
            </div>
            {brand.docs.length > 0 && (
              <div data-testid="brand-docs">
                <p className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-3">{t("common.docs")}</p>
                <ul className="space-y-2">
                  {brand.docs.map((d) => (
                    <li key={d.url}>
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 text-xs text-[#E8E6E1] hover:border-[#C2A675]/60 hover:text-[#C2A675] transition-colors">
                        <span className="flex items-center gap-2 min-w-0"><FileText size={13} className="shrink-0 text-[#C2A675]" aria-hidden="true" /><span className="truncate">{d.label}</span></span>
                        <ExternalLink size={12} className="shrink-0" aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {brand.officialSite && (
              <a href={brand.officialSite} target="_blank" rel="noopener noreferrer" data-testid="brand-official-site" className="flex items-center justify-between border border-white/10 px-5 py-4 text-xs uppercase tracking-[0.16em] text-[#E8E6E1] hover:border-[#C2A675]/60 hover:text-[#C2A675] transition-colors">
                {t("common.official_site")} <ExternalLink size={13} aria-hidden="true" />
              </a>
            )}
            <Link to={`${lp("/orcamento")}?marca=${slug}`} data-testid="brand-quote-cta" className="btn-solid w-full py-4">
              {t("common.request_quote")} <ArrowRight size={14} aria-hidden="true" />
            </Link>
            <Link to={`${lp("/contactos")}?marca=${slug}&assunto=info`} data-testid="brand-info-cta" className="btn-ghost w-full py-4">
              {t("common.request_info")}
            </Link>
            <Link to={`${lp("/contactos")}?marca=${slug}&assunto=amostras`} data-testid="brand-catalogue-cta" className="block text-center text-[11px] uppercase tracking-[0.16em] text-[#A8A6A0] hover:text-[#C2A675] transition-colors">
              {t("contact.assuntos.amostras")} →
            </Link>
          </Reveal>
        </aside>
      </section>

      <section className="border-t border-white/10 bg-[#0E0F12]">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <Reveal className="mono-tag text-[11px] uppercase tracking-[0.22em] text-[#C2A675] mb-6">{t("common.brands_label")}</Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {others.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <Link to={lp(`/marcas/${b.slug}`)} data-testid={`related-brand-${b.slug}`} className="group flex items-center gap-4 border border-white/10 p-3 hover:border-[#C2A675]/50 transition-colors">
                  <div className="img-frame w-20 h-16 shrink-0"><Img id={b.image} alt={b.name} small className="absolute inset-0 w-full h-full object-cover img-tone" /></div>
                  <div className="min-w-0">
                    <p className="serif-display text-xl text-white font-light">{b.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[#A8A6A0] truncate">{t(`brand.${b.slug}.tag`)}</p>
                  </div>
                  <ArrowRight size={14} className="ml-auto text-[#A8A6A0] group-hover:text-[#C2A675] group-hover:translate-x-1 transition-all shrink-0" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
